import React from 'react';
import { MapPin, Building, CreditCard, Copy } from 'lucide-react';

const AddressStep = ({ formData, updateFormData, errors, setErrors }) => {
  const handleInputChange = (section, field, value) => {
    const newData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value
      }
    };
    updateFormData(newData);
    
    // Clear error when user starts typing
    const errorKey = `${section}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleSameAsRegistered = (checked) => {
    if (checked) {
      const newData = {
        ...formData,
        warehouseAddress: {
          ...formData.registeredAddress,
          isSameAsRegistered: true
        }
      };
      updateFormData(newData);
    } else {
      const newData = {
        ...formData,
        warehouseAddress: {
          isSameAsRegistered: false,
          street: '',
          landmark: '',
          city: '',
          state: '',
          pincode: ''
        }
      };
      updateFormData(newData);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Address</h2>
        <p className="text-gray-600">Provide your registered business address</p>
      </div>

      {/* Registered Address */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-blue-600" />
          Registered Business Address
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address *
            </label>
            <textarea
              value={formData.registeredAddress.street}
              onChange={(e) => handleInputChange('registeredAddress', 'street', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors['registeredAddress.street'] ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter complete street address"
              rows="3"
            />
            {errors['registeredAddress.street'] && (
              <p className="mt-1 text-sm text-red-600">{errors['registeredAddress.street']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Landmark
            </label>
            <input
              type="text"
              value={formData.registeredAddress.landmark}
              onChange={(e) => handleInputChange('registeredAddress', 'landmark', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nearby landmark"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              value={formData.registeredAddress.city}
              onChange={(e) => handleInputChange('registeredAddress', 'city', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors['registeredAddress.city'] ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="City name"
            />
            {errors['registeredAddress.city'] && (
              <p className="mt-1 text-sm text-red-600">{errors['registeredAddress.city']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State *
            </label>
            <select
              value={formData.registeredAddress.state}
              onChange={(e) => handleInputChange('registeredAddress', 'state', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors['registeredAddress.state'] ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select state</option>
              {indianStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors['registeredAddress.state'] && (
              <p className="mt-1 text-sm text-red-600">{errors['registeredAddress.state']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pincode *
            </label>
            <input
              type="text"
              value={formData.registeredAddress.pincode}
              onChange={(e) => handleInputChange('registeredAddress', 'pincode', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors['registeredAddress.pincode'] ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="6-digit pincode"
              maxLength="6"
            />
            {errors['registeredAddress.pincode'] && (
              <p className="mt-1 text-sm text-red-600">{errors['registeredAddress.pincode']}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Important Information
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Ensure address details match your GST registration</li>
                <li>This address will be used for business verification</li>
                <li>Additional details like warehouse address and bank details can be added later from your profile</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
